import * as THREE from 'three/webgpu';
import type {Viewer} from '../../viewer/Viewer';

/**
 * 草图平面鼠标求交解析器。
 *
 * 设计说明：
 * 1. 本类只负责把视口鼠标位置投影到给定草图平面，职责中不包含任何点捕捉或关系推断；
 * 2. Move/Copy Entities 可以在自由求交结果之上继续叠加既有捕捉策略；
 * 3. Ctrl+V 剪贴板粘贴直接消费自由求交结果，从结构上保证不会误用点捕捉链路。
 */
export class SketchPlanePointerIntersectionResolver {
    /** 复用的屏幕标准化坐标，避免鼠标热路径反复创建对象。 */
    private readonly pointerNdc = new THREE.Vector2();
    /** 复用的射线解析器。 */
    private readonly raycaster = new THREE.Raycaster();
    /** 复用的平面交点缓冲。 */
    private readonly intersection = new THREE.Vector3();

    /**
     * 解析视口鼠标位置与指定草图平面的自由交点。
     *
     * @param viewer 当前 Viewer。
     * @param plane 目标草图平面。
     * @param clientX 鼠标客户端 X 坐标。
     * @param clientY 鼠标客户端 Y 坐标。
     * @returns 世界坐标交点副本；缺少视口条件或无法求交时返回 null。
     */
    public resolve(
        viewer: Viewer,
        plane: THREE.Plane,
        clientX: number,
        clientY: number,
    ): THREE.Vector3 | null {
        if (!viewer.container || !viewer.camera) {
            return null;
        }

        const rect = viewer.container.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) {
            return null;
        }

        // 这里只进行屏幕坐标到射线的数学映射，不读取 PointSnapManager。
        this.pointerNdc.set(
            ((clientX - rect.left) / rect.width) * 2 - 1,
            -((clientY - rect.top) / rect.height) * 2 + 1,
        );
        this.raycaster.setFromCamera(this.pointerNdc, viewer.camera);
        const intersection = this.raycaster.ray.intersectPlane(plane, this.intersection);
        return intersection?.clone() ?? null;
    }
}
